"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";

interface SignInProps {
  inputClasses: string;
  showSignInPassword: boolean;
  setShowSignInPassword: (value: React.SetStateAction<boolean>) => void;
}

export default function SignIn({
  inputClasses,
  showSignInPassword,
  setShowSignInPassword,
}: SignInProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { login, isLoading, error, clearError } = useAuth();

  // Clear error when inputs change
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [username, password]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await login(username, password);
  };

  return (
    <Card className="border-border/80 shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl">
          Login to Your Account
        </CardTitle>
        <CardDescription>
          Access your Virtual Power Plant console.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-5" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="signin-username">Username</Label>
            <input
              id="signin-username"
              type="text"
              placeholder="admin"
              className={inputClasses}
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="signin-password">Password</Label>
            <div className="relative">
              <input
                id="signin-password"
                type={showSignInPassword ? "text" : "password"}
                placeholder="Enter your password"
                className={`${inputClasses} pr-12`}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-2 grid place-content-center rounded-md px-2 text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                onClick={() => setShowSignInPassword((prev: boolean) => !prev)}
                aria-label={
                  showSignInPassword ? "Hide password" : "Show password"
                }
              >
                {showSignInPassword ? (
                  <EyeOff className="size-5" />
                ) : (
                  <Eye className="size-5" />
                )}
              </button>
            </div>
          </div>

          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : null}

          <Button type="submit" className="w-fit px-6" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Login"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
